// hooks/useCompassHeading.js
import { useEffect, useRef, useState } from "react";

/** Returns {heading, accuracy, supported, permission} where
 * heading is degrees clockwise from North (0..360).
 */
export default function useCompassHeading() {
  const [state, setState] = useState({
    heading: null,
    accuracy: null,
    supported: false,
    permission: "unknown", // "unknown" | "granted" | "denied"
  });
  const raf = useRef(0);

  useEffect(() => {
    let cleanup = () => {};

    async function start() {
      const hasDO = "DeviceOrientationEvent" in window;
      const hasAbs = "ondeviceorientationabsolute" in window;
      const hasSO = "AbsoluteOrientationSensor" in window;

      if (!hasDO && !hasSO) {
        setState((s) => ({ ...s, supported: false, permission: "denied" }));
        return;
      }

      // iOS 13+ requires a user gesture + permission request
      if (
        typeof DeviceOrientationEvent?.requestPermission === "function" &&
        state.permission === "unknown"
      ) {
        try {
          const perm = await DeviceOrientationEvent.requestPermission();
          setState((s) => ({
            ...s,
            permission: perm === "granted" ? "granted" : "denied",
          }));
          if (perm !== "granted") return;
        } catch {
          setState((s) => ({ ...s, permission: "denied" }));
          return;
        }
      } else {
        setState((s) => ({ ...s, permission: "granted" }));
      }

      const onDO = (ev) => {
        // iOS Safari provides webkitCompassHeading (0 = North, clockwise)
        let heading = null;
        let accuracy = null;

        const screenAngle = window.screen.orientation?.angle || 0;

        if (typeof ev.webkitCompassHeading === "number") {
          heading = ev.webkitCompassHeading; // already compass degrees
          accuracy = ev.webkitCompassAccuracy ?? null;
          // Compensate for screen rotation (portrait/landscape)
          heading = (heading + screenAngle) % 360;
        } else if (typeof ev.alpha === "number") {
          // On many Android/Chrome “absolute” events, alpha is rotation about Z
          // Convert to compass: 0 = North, clockwise
          // Typical transform is heading = 360 - alpha
          let h = 360 - ev.alpha;
          h = (h + screenAngle) % 360;
          if (h < 0) h += 360;
          heading = h;
        }

        if (heading != null && !Number.isNaN(heading)) {
          // simple throttling via rAF to avoid spam
          if (!raf.current) {
            raf.current = requestAnimationFrame(() => {
              setState({
                heading,
                accuracy,
                supported: true,
                permission: "granted",
              });
              raf.current = 0;
            });
          }
        }
      };

      // Prefer absolute if available
      window.addEventListener(
        hasAbs ? "deviceorientationabsolute" : "deviceorientation",
        onDO,
        {
          capture: false,
          passive: true,
        },
      );

      cleanup = () => {
        window.removeEventListener(
          hasAbs ? "deviceorientationabsolute" : "deviceorientation",
          onDO,
          { capture: false },
        );
        if (raf.current) cancelAnimationFrame(raf.current);
      };
    }

    start();
    return () => cleanup();
  }, []);

  return state;
}
